import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Users, Bell, Loader2 } from "lucide-react";
import { 
  upsertRSVP, 
  getUserRSVP,
  getRSVPCounts, 
  listAttendees, 
  subscribeRSVP, 
  type RSVPChoice,
  type RSVPCounts,
  type Attendee
} from "../services/rsvp";
import useAuth from "../hooks/useAuth";
import { pushGA } from "../lib/utils";

interface EventRow {
  id: string;
  title: string;
  start_iso?: string;
  end_iso?: string;
}

interface RSVPBlockProps {
  event: EventRow;
  onShowReminder?: () => void; // callback para abrir modal "Me lembra"
}

export default function RSVPBlock({ event, onShowReminder }: RSVPBlockProps) {
  const { user, session } = useAuth();
  const [currentRSVP, setCurrentRSVP] = useState<RSVPChoice | null>(null);
  const [rsvpName, setRsvpName] = useState<string>("");
  const [counts, setCounts] = useState<RSVPCounts>({ yes: 0, maybe: 0, no: 0 });
  const [attendees, setAttendees] = useState<Attendee[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);

  // Pré-preencher nome com dados do perfil
  useEffect(() => {
    if (user || session) {
      const email = session?.user?.email ?? "";
      const name = (
        user?.display_name?.trim() ||
        session?.user?.user_metadata?.full_name?.trim() ||
        (email ? email.split("@")[0] : "")
      ).trim();
      setRsvpName(name);
    }
  }, [user, session]);

  // Buscar dados iniciais
  const refreshData = async () => {
    try {
      const [userRsvp, eventCounts, eventAttendees] = await Promise.all([
        getUserRSVP(event.id),
        getRSVPCounts(event.id),
        listAttendees(event.id, 5)
      ]);
      
      setCurrentRSVP(userRsvp?.choice || null);
      if (userRsvp?.name) {
        setRsvpName(userRsvp.name);
      }
      setCounts(eventCounts);
      setAttendees(eventAttendees);
    } catch (error) {
      console.error("Erro ao carregar dados do RSVP:", error);
    }
  };

  useEffect(() => {
    setLoading(true);
    refreshData().finally(() => setLoading(false));
    
    // Subscription para atualizações em tempo real
    const unsubscribe = subscribeRSVP(event.id, refreshData);
    return unsubscribe;
  }, [event.id]);

  const handleRSVP = async (choice: RSVPChoice) => {
    if (submitting) return;
    
    setSubmitting(true);
    try {
      await upsertRSVP(event.id, choice, rsvpName || undefined);
      setCurrentRSVP(choice);
      
      pushGA('rsvp_submit', { 
        event_id: event.id, 
        choice,
        event_title: event.title 
      });
      
      // Refresh para pegar contagens atualizadas
      await refreshData();
    } catch (error) {
      console.error("Erro ao salvar RSVP:", error);
      alert("Erro ao salvar sua resposta. Tente novamente.");
    } finally {
      setSubmitting(false);
    }
  };

  const getButtonStyle = (choice: RSVPChoice) => {
    const isSelected = currentRSVP === choice;
    const baseClasses = "rounded-lg px-3 py-2 text-sm font-exo font-medium border transition-all duration-200 disabled:opacity-50";
    
    if (choice === "yes") {
      return `${baseClasses} ${
        isSelected
          ? "bg-green-500 text-white border-green-500 shadow-md"
          : "bg-white dark:bg-soft-black text-soft-black dark:text-white border-light-gray-alt dark:border-deep-purple/50 hover:bg-green-500/10 dark:hover:bg-green-500/20 hover:border-green-500/30"
      }`;
    }
    
    if (choice === "maybe") {
      return `${baseClasses} ${
        isSelected
          ? "bg-sun-flower text-white border-sun-flower shadow-md"
          : "bg-white dark:bg-soft-black text-soft-black dark:text-white border-light-gray-alt dark:border-deep-purple/50 hover:bg-sun-flower/10 dark:hover:bg-sun-flower/20 hover:border-sun-flower/30"
      }`;
    }
    
    // choice === "no"
    return `${baseClasses} ${
      isSelected
        ? "bg-red-500 text-white border-red-500 shadow-md"
        : "bg-white dark:bg-soft-black text-soft-black dark:text-white border-light-gray-alt dark:border-deep-purple/50 hover:bg-red-500/10 dark:hover:bg-red-500/20 hover:border-red-500/30"
    }`;
  };

  if (loading) {
    return (
      <div className="w-full rounded-xl border border-sun-flower/40 bg-sun-flower/10 dark:bg-sun-flower/20 px-4 py-4">
        <div className="flex items-center justify-center gap-2 text-sm text-soft-black/70 dark:text-light-gray/80 font-exo">
          <Loader2 className="h-4 w-4 animate-spin" />
          Carregando...
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`w-full rounded-xl border px-4 py-4 ${
        currentRSVP === 'yes' 
          ? 'border-green-500/30 bg-green-500/5 dark:bg-green-500/10' 
          : currentRSVP === 'maybe'
          ? 'border-sun-flower/40 bg-sun-flower/10 dark:bg-sun-flower/20'
          : currentRSVP === 'no'
          ? 'border-red-500/30 bg-red-500/5 dark:bg-red-500/10'
          : 'border-sun-flower/40 bg-sun-flower/10 dark:bg-sun-flower/20'
      }`}
    >
      {/* Header */}
      <div className="flex items-center justify-between text-sm mb-3">
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-sun-flower" />
          <span className="text-soft-black/80 dark:text-light-gray/95 font-exo font-medium">
            Sua presença
          </span>
        </div>
        <div className="text-xs text-soft-black/60 dark:text-light-gray/80 font-exo">
          {counts.yes} vão · {counts.maybe} talvez · {counts.no} não
        </div>
      </div>

      {/* Nome (opcional) */}
      <div className="mb-3">
        <input
          type="text"
          placeholder="Seu nome (opcional para aparecer na lista)"
          className="w-full rounded-lg border border-light-gray-alt dark:border-deep-purple/50 bg-white dark:bg-soft-black px-3 py-2 text-sm font-exo text-soft-black dark:text-light-gray/95 placeholder:text-soft-black/50 dark:placeholder:text-light-gray/60 focus:ring-2 focus:ring-sun-flower/20 focus:border-sun-flower/50 transition-all"
          value={rsvpName}
          onChange={(e) => setRsvpName(e.target.value)}
          disabled={submitting}
        />
      </div>

      {/* Botões de escolha */}
      <div className="grid grid-cols-3 gap-2 mb-3">
        <button
          onClick={() => handleRSVP("yes")}
          disabled={submitting}
          className={getButtonStyle("yes")}
        >
          {submitting && currentRSVP !== "yes" ? (
            <Loader2 className="h-3 w-3 animate-spin mx-auto" />
          ) : (
            "Eu vou"
          )}
        </button>
        
        <button
          onClick={() => handleRSVP("maybe")}
          disabled={submitting}
          className={getButtonStyle("maybe")}
        >
          {submitting && currentRSVP !== "maybe" ? (
            <Loader2 className="h-3 w-3 animate-spin mx-auto" />
          ) : (
            "Talvez"
          )}
        </button>
        
        <button
          onClick={() => handleRSVP("no")}
          disabled={submitting}
          className={getButtonStyle("no")}
        >
          {submitting && currentRSVP !== "no" ? (
            <Loader2 className="h-3 w-3 animate-spin mx-auto" />
          ) : (
            "Não vou"
          )}
        </button>
      </div>

      {/* Botão "Me lembra" para yes/maybe */}
      {(currentRSVP === "yes" || currentRSVP === "maybe") && onShowReminder && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          className="mb-3"
        >
          <button
            onClick={() => {
              onShowReminder();
              pushGA('reminder_from_rsvp', { 
                event_id: event.id, 
                rsvp_choice: currentRSVP 
              });
            }}
            className="w-full inline-flex items-center justify-center gap-2 rounded-lg border border-darling-lilac/30 bg-gradient-to-r from-darling-lilac/10 to-deep-purple/10 dark:from-darling-lilac/20 dark:to-deep-purple/20 hover:from-darling-lilac/20 hover:to-deep-purple/20 dark:hover:from-darling-lilac/30 dark:hover:to-deep-purple/30 px-3 py-2 text-sm font-exo font-semibold text-darling-lilac dark:text-darling-lilac transition-all duration-200 shadow-sm hover:shadow-md"
          >
            <Bell className="h-4 w-4" />
            Me lembra deste evento
          </button>
        </motion.div>
      )}

      {/* Lista de confirmados */}
      {attendees.length > 0 && (
        <div className="text-xs text-darling-lilac dark:text-darling-lilac/90 font-exo">
          <span className="font-medium">Confirmados (eu vou):</span>{" "}
          {attendees
            .filter(a => a.name)
            .slice(0, 3)
            .map(a => a.name)
            .join(", ")}
          {attendees.length > 3 && ` (+${attendees.length - 3})`}
        </div>
      )}
    </motion.div>
  );
}